import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, sendEmailVerification, sendPasswordResetEmail, deleteUser as deleteAuthUser } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, getDocs, query, orderBy, updateDoc, increment, addDoc, deleteDoc, where } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// ============================================
// FUNÇÕES DE AUTENTICAÇÃO
// ============================================

export const registerUser = async (email, password, name) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await sendEmailVerification(user);

    await setDoc(doc(db, 'users', user.uid), {
      name,
      email,
      points: 0,
      level: 'Iniciante',
      activities: 0,
      trails: 0,
      isAdmin: false,
      createdAt: new Date().toISOString()
    });

    return user;
  } catch (error) {
    throw error;
  }
};

export const resendVerificationEmail = async () => {
  try {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      await sendEmailVerification(auth.currentUser);
    }
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

// ============================================
// FUNÇÕES DE BASE DE DADOS (UTILIZADOR)
// ============================================

export const getUserData = async (uid) => {
  try {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    throw error;
  }
};

export const getNews = async () => {
  try {
    const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    // Fallback sem ordenação se o índice não existir
    try {
      const querySnapshot = await getDocs(collection(db, 'news'));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (fallbackError) {
      throw fallbackError;
    }
  }
};

export const getOffers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'offers'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const getActivities = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'activities'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const redeemOffer = async (uid, offer, user) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      points: increment(-(offer?.points || 0))
    });

    await addDoc(collection(db, 'userRedemptions'), {
      userId: uid,
      userName: user?.name || 'Utilizador',
      userEmail: user?.email || '',
      offerId: offer?.id || null,
      offerTitle: offer?.title || '',
      pointsCost: offer?.points || 0,
      status: 'pending',   // pending -> sent
      voucher: '',
      redeemedAt: new Date().toISOString(),
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    throw error;
  }
};

// Admin: obter todos os pedidos de resgate
export const getRedemptions = async () => {
  try {
    const q = query(collection(db, 'userRedemptions'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    const snap = await getDocs(collection(db, 'userRedemptions'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
};

// Admin: atualizar um resgate (marcar como enviado, guardar voucher/nota)
export const updateRedemption = async (id, data) => {
  const ref = doc(db, 'userRedemptions', id);
  await updateDoc(ref, data);
};

// ============================================
// DEFINIÇÕES GLOBAIS (settings/app) — geridas pelo admin
// ============================================
export const getAppSettings = async () => {
  try {
    const snap = await getDoc(doc(db, 'settings', 'app'));
    return snap.exists() ? snap.data() : {};
  } catch (e) {
    console.error('Erro ao ler definições:', e);
    return {};
  }
};

export const updateAppSettings = async (data) => {
  await setDoc(doc(db, 'settings', 'app'), data, { merge: true });
};

// ============================================
// PRÉMIO DA META MENSAL
// Corre no browser do admin (ao concluir uma atividade), porque só ele tem
// permissão para aumentar pontos. Atribuído no máximo uma vez por mês.
// ============================================
const monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const isGoalComplete = (user, monthActivities, activitiesById, totalCategories) => {
  const type = user?.goalType || 'monthly_count';
  if (type === 'weekly_one') {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const weeks = Math.ceil((lastDay + offset) / 7);
    const done = new Set(monthActivities.map((h) => {
      const d = new Date(h.completedAt);
      return Math.floor((d.getDate() + offset - 1) / 7);
    }));
    return done.size >= weeks;
  }
  if (type === 'all_categories') {
    const cats = new Set(monthActivities.map((h) => activitiesById[h.activityId]?.categoryId).filter(Boolean));
    return cats.size >= totalCategories;
  }
  const target = Math.max(1, parseInt(user?.goalTarget) || 4);
  return monthActivities.length >= target;
};

// Verifica se o utilizador cumpriu a meta do mês e, em caso afirmativo, atribui o prémio.
export const maybeAwardMonthlyGoal = async (userId) => {
  try {
    const settings = await getAppSettings();
    const reward = parseInt(settings.goalRewardPoints) || 0;
    if (reward <= 0) return null;

    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) return null;
    const user = userSnap.data();

    const key = monthKey();
    if (user.goalAwardedMonth === key) return null; // já premiado este mês

    // Atividades concluídas pelo utilizador no mês corrente
    const histSnap = await getDocs(query(collection(db, 'userActivities'), where('userId', '==', userId)));
    const start = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const monthActivities = histSnap.docs
      .map((d) => d.data())
      .filter((h) => h.completedAt && new Date(h.completedAt) >= start);

    // Categorias, apenas necessárias para a meta "uma de cada tipo"
    let activitiesById = {};
    let totalCategories = 6;
    if ((user.goalType || 'monthly_count') === 'all_categories') {
      const actsSnap = await getDocs(collection(db, 'activities'));
      actsSnap.docs.forEach((d) => { activitiesById[d.id] = d.data(); });
      const catsSnap = await getDocs(collection(db, 'activityCategories'));
      if (catsSnap.size > 0) totalCategories = catsSnap.size;
    }

    if (!isGoalComplete(user, monthActivities, activitiesById, totalCategories)) return null;

    await updateDoc(doc(db, 'users', userId), {
      points: increment(reward),
      goalAwardedMonth: key,
      goalAwardedAt: new Date().toISOString(),
    });

    return { reward, userName: user.name || 'Utilizador' };
  } catch (e) {
    console.error('Erro ao atribuir prémio da meta:', e);
    return null;
  }
};

export const completeActivity = async (userId, activityId, pointsEarned, registrationId = null) => {
  try {
    console.log('🎯 Marcando atividade como concluída:', { userId, activityId, pointsEarned });
    
    // Descobrir a categoria da atividade para contabilizar por tipo
    let categoryId = null;
    try {
      const actSnap = await getDoc(doc(db, 'activities', activityId));
      if (actSnap.exists()) categoryId = actSnap.data().categoryId || null;
    } catch (e) {
      console.error('Não foi possível ler a categoria da atividade:', e);
    }

    // Atualizar pontos e contadores do utilizador (total e por categoria)
    const userRef = doc(db, 'users', userId);
    const updates = {
      points: increment(pointsEarned),
      activities: increment(1)
    };
    if (categoryId) updates[`categoryCounts.${categoryId}`] = increment(1);
    await updateDoc(userRef, updates);

    // Registar a atividade completada no histórico
    await addDoc(collection(db, 'userActivities'), {
      userId,
      activityId,
      pointsEarned,
      completedAt: new Date().toISOString()
    });

    // Se veio de uma inscrição, marcar a inscrição como concluída
    if (registrationId) {
      const registrationRef = doc(db, 'activityRegistrations', registrationId);
      await updateDoc(registrationRef, {
        completed: true,
        completedAt: new Date().toISOString()
      });
    }

    // Se esta conclusão fechou a meta mensal, atribuir o prémio definido pelo admin
    let goalAward = null;
    try {
      goalAward = await maybeAwardMonthlyGoal(userId);
    } catch (e) {
      console.error('Prémio da meta falhou:', e);
    }

    // Atualizar já a entrada do utilizador no ranking (sem esperar que ele abra a app)
    try {
      const freshUser = await getDoc(userRef);
      if (freshUser.exists()) await syncLeaderboardEntry(userId, freshUser.data());
    } catch (e) {
      console.error('Sync ranking falhou:', e);
    }

    console.log('✅ Atividade marcada como concluída!');
    return goalAward; // { reward, userName } quando houve prémio
  } catch (error) {
    console.error('❌ Erro ao completar atividade:', error);
    throw error;
  }
};

// Obter histórico de atividades completadas do utilizador
export const getUserActivityHistory = async (userId) => {
  try {
    const q = query(
      collection(db, 'userActivities'),
      orderBy('completedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const allActivities = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return allActivities.filter(activity => activity.userId === userId);
  } catch (error) {
    console.error('Erro ao obter histórico:', error);
    return [];
  }
};

// ============================================
// FUNÇÕES ADMIN
// ============================================

// Obter todos os utilizadores
export const getAllUsers = async () => {
  const querySnapshot = await getDocs(collection(db, 'users'));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// ============================================
// RANKING (coleção `leaderboard`)
// Coleção separada e pública apenas com dados não sensíveis (nome, atividades,
// pontos, nível, foto). Os emails e dados de pagamento ficam protegidos em `users`.
// ============================================

// Espelha os dados públicos de um utilizador no ranking.
export const syncLeaderboardEntry = async (userId, user) => {
  if (!userId || !user || user.isAdmin) return;
  await setDoc(doc(db, 'leaderboard', userId), {
    name: user.name || 'Utilizador',
    activities: user.activities || 0,
    trails: user.trails || 0,
    points: user.points || 0,
    categoryCounts: user.categoryCounts || {},
    level: user.level || 'Iniciante',
    photoURL: user.photoURL || null,
    isPremium: !!user.isPremium,
    seeded: !!user.seeded,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
};

export const removeLeaderboardEntry = async (userId) => {
  await deleteDoc(doc(db, 'leaderboard', userId));
};

// Devolve todas as entradas do ranking. A ordenação (total ou por categoria)
// é feita na interface, conforme a escolha do utilizador.
export const getLeaderboard = async (max = 500) => {
  const snapshot = await getDocs(collection(db, 'leaderboard'));
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .slice(0, max);
};

// ============================================
// CONQUISTAS (coleção `achievements`) — CRUD do admin
// ============================================

export const getAchievements = async () => {
  const snapshot = await getDocs(collection(db, 'achievements'));
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

export const addAchievement = async (data) => {
  await addDoc(collection(db, 'achievements'), {
    ...data,
    createdAt: new Date().toISOString(),
  });
};

export const updateAchievement = async (id, data) => {
  await updateDoc(doc(db, 'achievements', id), data);
};

export const deleteAchievement = async (id) => {
  await deleteDoc(doc(db, 'achievements', id));
};

// Importa uma lista de conquistas de origem (usado para criar as predefinidas)
export const seedAchievements = async (list) => {
  let added = 0;
  for (const a of list) {
    await addDoc(collection(db, 'achievements'), {
      ...a,
      active: true,
      createdAt: new Date().toISOString(),
    });
    added++;
  }
  return { added };
};

// Admin: reconstrói todo o ranking a partir dos utilizadores (corrige desatualizações).
export const rebuildLeaderboard = async () => {
  const snapshot = await getDocs(collection(db, 'users'));
  let synced = 0;
  for (const d of snapshot.docs) {
    const u = d.data();
    if (u.isAdmin) continue;
    await syncLeaderboardEntry(d.id, u);
    synced++;
  }
  return { synced };
};

// Atualizar utilizador
export const updateUser = async (userId, data) => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, data);
};

// Apagar utilizador
export const deleteUser = async (userId) => {
  await deleteDoc(doc(db, 'users', userId));
};

// RGPD — o próprio utilizador apaga a conta e todos os seus dados pessoais.
// Remove: perfil, entrada no ranking, inscrições, histórico, resgates e recompensas.
export const deleteMyAccount = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Sessão não iniciada.');
  const uid = user.uid;

  // Apagar registos pessoais espalhados pelas coleções
  const personal = [
    { name: 'activityRegistrations', field: 'userId' },
    { name: 'userActivities', field: 'userId' },
    { name: 'userRedemptions', field: 'userId' },
    { name: 'challengeRegistrations', field: 'userId' },
    { name: 'userRewards', field: 'userId' },
  ];
  for (const c of personal) {
    try {
      const snap = await getDocs(query(collection(db, c.name), where(c.field, '==', uid)));
      for (const d of snap.docs) {
        await deleteDoc(doc(db, c.name, d.id));
      }
    } catch (e) {
      console.error(`Não foi possível limpar ${c.name}:`, e);
    }
  }

  try { await deleteDoc(doc(db, 'leaderboard', uid)); } catch (e) { /* pode não existir */ }
  await deleteDoc(doc(db, 'users', uid));

  // Por fim, a própria conta de autenticação
  await deleteAuthUser(user);
};

// Checklist pessoal de tarefas de uma inscrição (o user marca as tarefas que já fez)
export const updateRegistrationChecklist = async (registrationId, completedTasks) => {
  const ref = doc(db, 'activityRegistrations', registrationId);
  await updateDoc(ref, { completedTasks });
};

// Adicionar notícia
export const addNews = async (newsData) => {
  await addDoc(collection(db, 'news'), {
    ...newsData,
    createdAt: new Date().toISOString()
  });
};

// Atualizar notícia
export const updateNews = async (newsId, newsData) => {
  const newsRef = doc(db, 'news', newsId);
  await updateDoc(newsRef, newsData);
};

// Apagar notícia
export const deleteNews = async (newsId) => {
  await deleteDoc(doc(db, 'news', newsId));
};

// Adicionar oferta
export const addOffer = async (offerData) => {
  await addDoc(collection(db, 'offers'), offerData);
};

// Atualizar oferta
export const updateOffer = async (offerId, offerData) => {
  const offerRef = doc(db, 'offers', offerId);
  await updateDoc(offerRef, offerData);
};

// Apagar oferta
export const deleteOffer = async (offerId) => {
  await deleteDoc(doc(db, 'offers', offerId));
};

// Adicionar atividade
export const addActivity = async (activityData) => {
  await addDoc(collection(db, 'activities'), activityData);
};

// Atualizar atividade
export const updateActivity = async (activityId, activityData) => {
  const activityRef = doc(db, 'activities', activityId);
  await updateDoc(activityRef, activityData);
};

// Apagar atividade
export const deleteActivity = async (activityId) => {
  await deleteDoc(doc(db, 'activities', activityId));
};

// ============================================
// CICLO DE VIDA DA ATIVIDADE (começar → decorrer → terminar → arquivar)
// ============================================

// Iniciar: fecha inscrições e marca como a decorrer
export const startActivity = async (activityId, admin) => {
  await updateDoc(doc(db, 'activities', activityId), {
    ongoing: true,
    archived: false,
    registrationsClosed: true,
    startedAt: new Date().toISOString(),
    startedByName: admin?.name || null,
    endedAt: null,
  });
};

// Definir/alterar a data prevista de fim
export const setActivityEndDate = async (activityId, plannedEndAt) => {
  await updateDoc(doc(db, 'activities', activityId), { plannedEndAt: plannedEndAt || null });
};

// Terminar: arquiva a atividade e fecha o estado de cada participante.
// Quem não foi marcado como concluído fica registado como não concluído.
export const endActivity = async (activityId) => {
  const now = new Date().toISOString();

  const q = query(collection(db, 'activityRegistrations'), where('activityId', '==', activityId));
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    const reg = d.data();
    if (reg.completed !== true) {
      await updateDoc(doc(db, 'activityRegistrations', d.id), {
        notCompleted: true,
        closedAt: now,
      });
    }
  }

  await updateDoc(doc(db, 'activities', activityId), {
    ongoing: false,
    archived: true,
    endedAt: now,
  });
};

// Reabrir uma atividade arquivada (caso o admin arquive por engano)
export const unarchiveActivity = async (activityId) => {
  await updateDoc(doc(db, 'activities', activityId), {
    archived: false,
    ongoing: false,
    registrationsClosed: false,
    endedAt: null,
  });
};

// ============================================
// FUNÇÕES DE INSCRIÇÕES EM ATIVIDADES
// ============================================

// Criar pedido de inscrição
export const createActivityRegistration = async (activityId, userId, userData, formData) => {
  try {
    console.log('📝 Criando inscrição para:', { activityId, userId });

    // Verificar se já existe inscrição
    const existingRegistrations = await getUserRegistrations(userId);
    const duplicateReg = existingRegistrations.find(
      reg => reg.activityId === activityId &&
        (reg.status === 'pending' || reg.status === 'approved')
    );

    if (duplicateReg) {
      throw new Error('Já existe uma inscrição para esta atividade!');
    }

    // Verificar se a atividade existe e tem vagas
    const activityRef = doc(db, 'activities', activityId);
    const activitySnap = await getDoc(activityRef);

    if (!activitySnap.exists()) {
      throw new Error('Atividade não encontrada!');
    }

    const activity = activitySnap.data();
    const availableSlots = activity.availableSlots || 0;

    console.log('📊 Vagas disponíveis:', availableSlots);

    if (availableSlots <= 0) {
      throw new Error('Não há vagas disponíveis para esta atividade!');
    }

    // Criar a inscrição
    await addDoc(collection(db, 'activityRegistrations'), {
      activityId,
      userId,
      userName: userData.name,
      userEmail: userData.email,
      userPhone: formData.phone || '',
      userAge: formData.age || '',
      emergencyContact: formData.emergencyContact || '',
      specialNeeds: formData.specialNeeds || '',
      status: 'pending',
      photoUrl: null,
      photoUploadedAt: null,
      photoStatus: null,
      completed: false,
      createdAt: new Date().toISOString(),
      approvedAt: null,
      approvedBy: null
    });

    console.log('✅ Inscrição criada com sucesso!');
  } catch (error) {
    console.error('❌ Erro ao criar inscrição:', error);
    throw error;
  }
};

// Obter todas as inscrições (Admin)
export const getAllRegistrations = async () => {
  try {
    const q = query(collection(db, 'activityRegistrations'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

// Obter inscrições por atividade
export const getRegistrationsByActivity = async (activityId) => {
  try {
    const q = query(
      collection(db, 'activityRegistrations'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const allRegistrations = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return allRegistrations.filter(reg => reg.activityId === activityId);
  } catch (error) {
    throw error;
  }
};

// Obter inscrições do utilizador
export const getUserRegistrations = async (userId) => {
  try {
    const q = query(collection(db, 'activityRegistrations'));
    const querySnapshot = await getDocs(q);
    const allRegistrations = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return allRegistrations.filter(reg => reg.userId === userId);
  } catch (error) {
    throw error;
  }
};

// Aprovar inscrição
export const approveRegistration = async (registrationId, adminId, activityId) => {
  try {
    console.log('🔍 Aprovando inscrição:', { registrationId, adminId, activityId });

    // Atualizar status da inscrição
    const registrationRef = doc(db, 'activityRegistrations', registrationId);
    await updateDoc(registrationRef, {
      status: 'approved',
      approvedAt: new Date().toISOString(),
      approvedBy: adminId
    });

    console.log('✅ Status atualizado');

    // Reduzir vagas disponíveis da atividade
    const activityRef = doc(db, 'activities', activityId);
    const activitySnap = await getDoc(activityRef);

    if (activitySnap.exists()) {
      const currentSlots = activitySnap.data().availableSlots || 0;
      console.log('📊 Vagas atuais:', currentSlots);

      if (currentSlots > 0) {
        await updateDoc(activityRef, {
          availableSlots: increment(-1)
        });
        console.log('✅ Vagas atualizadas');
      } else {
        console.warn('⚠️ Sem vagas disponíveis');
      }
    } else {
      console.error('❌ Atividade não encontrada:', activityId);
    }

    console.log('✅ Inscrição aprovada com sucesso!');
  } catch (error) {
    console.error('❌ Erro detalhado ao aprovar:', error);
    console.error('Código do erro:', error.code);
    console.error('Mensagem:', error.message);
    throw error;
  }
};

// Rejeitar inscrição
export const rejectRegistration = async (registrationId, adminId) => {
  try {
    console.log('🔍 Rejeitando inscrição:', { registrationId, adminId });

    const registrationRef = doc(db, 'activityRegistrations', registrationId);
    await updateDoc(registrationRef, {
      status: 'rejected',
      rejectedAt: new Date().toISOString(),
      rejectedBy: adminId
    });

    console.log('✅ Inscrição rejeitada com sucesso!');
  } catch (error) {
    console.error('❌ Erro ao rejeitar:', error);
    console.error('Código do erro:', error.code);
    console.error('Mensagem:', error.message);
    throw error;
  }
};

// Cancelar inscrição (pelo próprio utilizador)
export const cancelRegistration = async (registrationId, activityId) => {
  try {
    console.log('🔍 Iniciando cancelamento:', { registrationId, activityId });

    // Verificar se a atividade já foi concluída
    const registrationRef = doc(db, 'activityRegistrations', registrationId);
    const registrationSnap = await getDoc(registrationRef);

    if (!registrationSnap.exists()) {
      throw new Error('Inscrição não encontrada!');
    }

    const registration = registrationSnap.data();

    if (registration.completed === true) {
      throw new Error('❌ Não é possível cancelar uma atividade já concluída!');
    }

    // Devolver a vaga à atividade
    const activityRef = doc(db, 'activities', activityId);
    const activitySnap = await getDoc(activityRef);

    if (activitySnap.exists()) {
      console.log('📊 Atividade encontrada, devolvendo vaga...');
      await updateDoc(activityRef, {
        availableSlots: increment(1)
      });
      console.log('✅ Vaga devolvida');
    } else {
      console.warn('⚠️ Atividade não encontrada:', activityId);
    }

    // Deletar a inscrição
    console.log('🗑️ Deletando inscrição...');
    await deleteDoc(doc(db, 'activityRegistrations', registrationId));
    console.log('✅ Inscrição deletada');

    console.log('✅ Cancelamento concluído com sucesso!');
  } catch (error) {
    console.error('❌ Erro detalhado ao cancelar:', error);
    console.error('Código:', error.code);
    console.error('Mensagem:', error.message);
    throw error;
  }
};

// Obter contagem de inscrições pendentes
export const getPendingRegistrationsCount = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'activityRegistrations'));
    const registrations = querySnapshot.docs.map(doc => doc.data());
    return registrations.filter(reg => reg.status === 'pending').length;
  } catch (error) {
    return 0;
  }
};

// ============================================
// 🆕 FUNÇÕES DE UPLOAD DE FOTOS (IMGBB)
// ============================================

export const uploadActivityPhoto = async (imageFile) => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    const response = await fetch(
      `https://api.imgbb.com/1/upload?key=${process.env.REACT_APP_IMGBB_API_KEY}`,
      {
        method: 'POST',
        body: formData
      }
    );
    
    const data = await response.json();
    
    if (data.success) {
      return data.data.url;
    } else {
      throw new Error('Erro ao fazer upload da imagem');
    }
  } catch (error) {
    console.error('Erro no upload:', error);
    throw error;
  }
};

export const submitActivityPhoto = async (registrationId, photoUrl, location = null) => {
  try {
    const registrationRef = doc(db, 'activityRegistrations', registrationId);
    await updateDoc(registrationRef, {
      photoUrl: photoUrl,
      photoUploadedAt: new Date().toISOString(),
      photoStatus: 'pending',
      photoLocation: location
    });
  } catch (error) {
    throw error;
  }
};

export const approveActivityPhoto = async (registrationId) => {
  try {
    const registrationRef = doc(db, 'activityRegistrations', registrationId);
    await updateDoc(registrationRef, {
      photoStatus: 'approved'
    });
  } catch (error) {
    throw error;
  }
};

export const rejectActivityPhoto = async (registrationId) => {
  try {
    const registrationRef = doc(db, 'activityRegistrations', registrationId);
    await updateDoc(registrationRef, {
      photoStatus: 'rejected',
      photoUrl: null,
      photoUploadedAt: null
    });
  } catch (error) {
    throw error;
  }
};

// ============================================
// FUNÇÕES DE CATEGORIAS DE ATIVIDADES
// ============================================

// Obter todas as categorias
export const getActivityCategories = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'activityCategories'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Erro ao obter categorias:', error);
    return [];
  }
};

// Adicionar categoria
export const addActivityCategory = async (categoryData) => {
  try {
    await addDoc(collection(db, 'activityCategories'), categoryData);
  } catch (error) {
    throw error;
  }
};

// Atualizar categoria
export const updateActivityCategory = async (categoryId, categoryData) => {
  try {
    const categoryRef = doc(db, 'activityCategories', categoryId);
    await updateDoc(categoryRef, categoryData);
  } catch (error) {
    throw error;
  }
};

// Apagar categoria
export const deleteActivityCategory = async (categoryId) => {
  try {
    await deleteDoc(doc(db, 'activityCategories', categoryId));
  } catch (error) {
    throw error;
  }
};

// ============================================
// ESTATÍSTICAS GERAIS
// ============================================

export const getAdminStats = async () => {
  // Em paralelo — evita esperar por 8 leituras seguidas
  const [
    usersSnapshot,
    newsSnapshot,
    offersSnapshot,
    activitiesSnapshot,
    userActivitiesSnapshot,
    redemptionsSnapshot,
    challengesSnapshot,
    rewardsSnapshot,
  ] = await Promise.all([
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'news')),
    getDocs(collection(db, 'offers')),
    getDocs(collection(db, 'activities')),
    getDocs(collection(db, 'userActivities')),
    getDocs(collection(db, 'userRedemptions')),
    getDocs(collection(db, 'challenges')),
    getDocs(collection(db, 'rewardCatalog')),
  ]);

  const premiumUsers = usersSnapshot.docs.filter(doc => doc.data().isPremium === true).length;

  return {
    totalUsers: usersSnapshot.size,
    totalNews: newsSnapshot.size,
    totalOffers: offersSnapshot.size,
    totalActivities: activitiesSnapshot.size,
    totalCompletedActivities: userActivitiesSnapshot.size,
    totalRedemptions: redemptionsSnapshot.size,
    totalChallenges: challengesSnapshot.size,
    totalRewards: rewardsSnapshot.size,
    premiumUsers
  };
};

// ============================================
// FUNÇÕES PREMIUM
// ============================================

export const activatePremium = async (userId, stripeSessionId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isPremium: true,
      premiumSince: new Date().toISOString(),
      stripeSessionId: stripeSessionId || null,
      cancelAtPeriodEnd: false,
      premiumUntil: null
    });
  } catch (error) {
    throw error;
  }
};

export const deactivatePremium = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isPremium: false,
      stripeSessionId: null,
      cancelAtPeriodEnd: false,
      premiumUntil: null
    });
  } catch (error) {
    throw error;
  }
};

// Cancelamento self-service: o utilizador cancela no portal Stripe e mantém
// o Premium até ao fim do período pago (premiumUntil). A app expira depois.
export const markPremiumCancellation = async (userId, premiumUntil) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      cancelAtPeriodEnd: true,
      premiumUntil
    });
  } catch (error) {
    throw error;
  }
};

// ============================================
// CATÁLOGO DE RECOMPENSAS
// ============================================

export const getRewardCatalog = async () => {
  try {
    const q = query(collection(db, 'rewardCatalog'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    try {
      const querySnapshot = await getDocs(collection(db, 'rewardCatalog'));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (fallbackError) {
      throw fallbackError;
    }
  }
};

export const addReward = async (rewardData) => {
  try {
    await addDoc(collection(db, 'rewardCatalog'), {
      ...rewardData,
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    throw error;
  }
};

export const updateReward = async (rewardId, rewardData) => {
  try {
    const rewardRef = doc(db, 'rewardCatalog', rewardId);
    await updateDoc(rewardRef, rewardData);
  } catch (error) {
    throw error;
  }
};

export const deleteReward = async (rewardId) => {
  try {
    await deleteDoc(doc(db, 'rewardCatalog', rewardId));
  } catch (error) {
    throw error;
  }
};

// ============================================
// DESAFIOS (CHALLENGES)
// ============================================

export const getChallenges = async () => {
  try {
    const q = query(collection(db, 'challenges'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    try {
      const querySnapshot = await getDocs(collection(db, 'challenges'));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (fallbackError) {
      throw fallbackError;
    }
  }
};

export const addChallenge = async (challengeData) => {
  try {
    await addDoc(collection(db, 'challenges'), {
      ...challengeData,
      active: true,
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    throw error;
  }
};

export const updateChallenge = async (challengeId, challengeData) => {
  try {
    const challengeRef = doc(db, 'challenges', challengeId);
    await updateDoc(challengeRef, challengeData);
  } catch (error) {
    throw error;
  }
};

export const deleteChallenge = async (challengeId) => {
  try {
    await deleteDoc(doc(db, 'challenges', challengeId));
  } catch (error) {
    throw error;
  }
};

// ============================================
// INSCRIÇÕES EM DESAFIOS
// ============================================

export const enrollInChallenge = async (userId, userName, challenge) => {
  try {
    const existing = await getUserChallengeRegistrations(userId);
    const duplicate = existing.find(
      reg => reg.challengeId === challenge.id &&
        (reg.status === 'active' || reg.status === 'completed')
    );
    if (duplicate) {
      throw new Error('Já está inscrito neste desafio!');
    }

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      points: increment(-challenge.pointsCost)
    });

    const taskProgress = challenge.tasks.map(task => ({
      taskId: task.id,
      taskOrder: task.order,
      status: task.order === 1 ? 'available' : 'locked',
      photoUrl: null,
      photoStatus: null,
      completedAt: null
    }));

    await addDoc(collection(db, 'challengeRegistrations'), {
      userId,
      userName,
      challengeId: challenge.id,
      challengeName: challenge.name,
      status: 'active',
      taskProgress,
      rewardsClaimed: false,
      enrolledAt: new Date().toISOString(),
      completedAt: null
    });
  } catch (error) {
    throw error;
  }
};

export const getUserChallengeRegistrations = async (userId) => {
  try {
    const q = query(collection(db, 'challengeRegistrations'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    throw error;
  }
};

export const getAllChallengeRegistrations = async () => {
  try {
    const q = query(collection(db, 'challengeRegistrations'), orderBy('enrolledAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    try {
      const querySnapshot = await getDocs(collection(db, 'challengeRegistrations'));
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (fallbackError) {
      throw fallbackError;
    }
  }
};

// ============================================
// TAREFAS DE DESAFIOS (FOTOS)
// ============================================

export const submitChallengeTaskPhoto = async (registrationId, taskId, photoUrl, location = null) => {
  try {
    const regRef = doc(db, 'challengeRegistrations', registrationId);
    const regSnap = await getDoc(regRef);
    if (!regSnap.exists()) throw new Error('Inscrição não encontrada');

    const data = regSnap.data();
    const updatedProgress = data.taskProgress.map(tp => {
      if (tp.taskId === taskId) {
        return { ...tp, photoUrl, photoStatus: 'pending', photoLocation: location };
      }
      return tp;
    });

    await updateDoc(regRef, { taskProgress: updatedProgress });
  } catch (error) {
    throw error;
  }
};

export const approveChallengeTaskPhoto = async (registrationId, taskId, taskPoints) => {
  try {
    const regRef = doc(db, 'challengeRegistrations', registrationId);
    const regSnap = await getDoc(regRef);
    if (!regSnap.exists()) throw new Error('Inscrição não encontrada');

    const data = regSnap.data();
    let currentTaskOrder = 0;

    const updatedProgress = data.taskProgress.map(tp => {
      if (tp.taskId === taskId) {
        currentTaskOrder = tp.taskOrder;
        return { ...tp, photoStatus: 'approved', status: 'completed', completedAt: new Date().toISOString() };
      }
      return tp;
    });

    const finalProgress = updatedProgress.map(tp => {
      if (tp.taskOrder === currentTaskOrder + 1 && tp.status === 'locked') {
        return { ...tp, status: 'available' };
      }
      return tp;
    });

    const allCompleted = finalProgress.every(tp => tp.status === 'completed');

    await updateDoc(regRef, {
      taskProgress: finalProgress,
      status: allCompleted ? 'completed' : 'active',
      completedAt: allCompleted ? new Date().toISOString() : null
    });

    const userRef = doc(db, 'users', data.userId);
    await updateDoc(userRef, {
      points: increment(taskPoints)
    });

    // Refletir os novos pontos no ranking imediatamente
    try {
      const freshUser = await getDoc(userRef);
      if (freshUser.exists()) await syncLeaderboardEntry(data.userId, freshUser.data());
    } catch (e) {
      console.error('Sync ranking falhou:', e);
    }
  } catch (error) {
    throw error;
  }
};

export const rejectChallengeTaskPhoto = async (registrationId, taskId) => {
  try {
    const regRef = doc(db, 'challengeRegistrations', registrationId);
    const regSnap = await getDoc(regRef);
    if (!regSnap.exists()) throw new Error('Inscrição não encontrada');

    const data = regSnap.data();
    const updatedProgress = data.taskProgress.map(tp => {
      if (tp.taskId === taskId) {
        return { ...tp, photoUrl: null, photoStatus: 'rejected', status: 'available' };
      }
      return tp;
    });

    await updateDoc(regRef, { taskProgress: updatedProgress });
  } catch (error) {
    throw error;
  }
};

// ============================================
// RECOMPENSAS DO UTILIZADOR
// ============================================

export const claimChallengeRewards = async (registrationId, userId, challenge) => {
  try {
    const regRef = doc(db, 'challengeRegistrations', registrationId);
    await updateDoc(regRef, { rewardsClaimed: true });

    for (const reward of challenge.rewards) {
      await addDoc(collection(db, 'userRewards'), {
        userId,
        challengeId: challenge.id,
        challengeName: challenge.name,
        rewardId: reward.rewardId,
        rewardName: reward.name,
        rewardImage: reward.image,
        earnedAt: new Date().toISOString()
      });
    }
  } catch (error) {
    throw error;
  }
};

export const getUserRewards = async (userId) => {
  try {
    const q = query(collection(db, 'userRewards'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Erro ao obter recompensas:', error);
    return [];
  }
};